import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { Cpu, RefreshCw, CheckCircle, Search } from 'lucide-react';

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchDevices = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE_URL}/api/admin/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve devices list.');
      }

      const result = await response.json();
      if (result.success) {
        setDevices(result.data);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to the API. Displaying simulation device matrix.');
      // Load fallback simulation data
      setDevices(getMockDevices());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMockDevices = () => {
    return [
      { id: '1', serial: 'FS-001', gymId: '1', gymName: 'Muscle Factory, Hyd', firmwareVersion: '2.0.4', lastSeenAt: new Date(Date.now() - 2 * 60000).toISOString(), status: 'Online' },
      { id: '2', serial: 'FS-002', gymId: '2', gymName: 'Iron House, Vijayawada', firmwareVersion: '2.0.1', lastSeenAt: new Date(Date.now() - 45 * 60000).toISOString(), status: 'Offline' },
      { id: '3', serial: 'FS-003', gymId: '3', gymName: 'FitZone, Guntur', firmwareVersion: '1.9.9', lastSeenAt: new Date(Date.now() - 26 * 3600000).toISOString(), status: 'Offline' },
      { id: '4', serial: 'FS-004', gymId: '4', gymName: 'Gold Gym, Vizag', firmwareVersion: '2.0.4', lastSeenAt: new Date(Date.now() - 5000).toISOString(), status: 'Online' }
    ];
  };

  // Filter devices based on search
  const filteredDevices = devices.filter(d => 
    d.serial.toLowerCase().includes(search.toLowerCase()) || 
    d.gymName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Scanner Hardware Status</h1>
          <p className="text-gray-400 text-sm mt-1">Audit active Raspberry Pi scanners and heartbeat logs.</p>
        </div>
        <button
          onClick={() => fetchDevices(true)}
          disabled={refreshing}
          className="flex items-center px-4 py-2.5 bg-[#1E293B] border border-[#334155] hover:border-[#6CD800] rounded-lg text-sm text-gray-300 transition-all hover:text-white"
        >
          <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin text-[#6CD800]' : ''}`} />
          Refresh Scanner Matrix
        </button>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4">
          {error}
        </div>
      )}

      {/* Filter and Search Panel */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-5 shadow-lg flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by serial ID or gym name..."
            className="w-full pl-10 pr-4 py-2 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6CD800] text-sm"
          />
          <div className="absolute left-3 top-2.5 text-gray-500">
            <Search size={18} />
          </div>
        </div>

        <div className="flex items-center space-x-6 text-xs text-gray-400">
          <span className="flex items-center"><CheckCircle size={14} className="text-[#6CD800] mr-1.5" /> Deployed Scanners: {devices.length}</span>
          <span className="flex items-center"><span className="h-2 w-2 rounded-full bg-[#6CD800] mr-1.5 animate-pulse"></span> Online: {devices.filter(d => d.status === 'Online').length}</span>
          <span className="flex items-center"><span className="h-2 w-2 rounded-full bg-red-500 mr-1.5"></span> Offline: {devices.filter(d => d.status === 'Offline').length}</span>
        </div>
      </div>

      {/* Main Grid: Devices */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg overflow-hidden">
        {loading && !refreshing ? (
          <div className="flex justify-center items-center h-48 text-[#6CD800]">
            <RefreshCw size={24} className="animate-spin mr-2" />
            Querying hardware network...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-300">
              <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
                <tr>
                  <th scope="col" className="px-6 py-4">Hardware Unit</th>
                  <th scope="col" className="px-6 py-4">Linked Gym</th>
                  <th scope="col" className="px-6 py-4">Firmware Version</th>
                  <th scope="col" className="px-6 py-4">Last Seen Heartbeat</th>
                  <th scope="col" className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">No active scanners found in system records.</td>
                  </tr>
                ) : (
                  filteredDevices.map((d) => {
                    const isOnline = d.status === 'Online';
                    return (
                      <tr key={d.id} className="hover:bg-[#1E293B]/20">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${isOnline ? 'bg-[#6CD800]/10 text-[#6CD800]' : 'bg-red-500/10 text-red-400'}`}>
                              <Cpu size={18} />
                            </div>
                            <div>
                              <span className="block text-sm font-mono font-bold text-white">{d.serial}</span>
                              <span className="block text-xs text-gray-500">ID: {d.id}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="block text-white font-medium">{d.gymName}</span>
                          <span className="block text-xs text-gray-400">Gym ID: {d.gymId || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-0.5 rounded font-mono text-xs bg-[#1E293B] text-gray-300 border border-gray-700">
                            v{d.firmwareVersion || '1.0.0'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-400">
                          {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : 'Never seen'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            isOnline 
                              ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20' 
                              : 'bg-red-500/10 text-red-400 border border-red-500/20'
                          }`}>
                            <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                              isOnline ? 'bg-[#6CD800] animate-pulse' : 'bg-red-500'
                            }`}></span>
                            {d.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Devices;
